import { useCallback, useEffect, useState } from "react";

export const RECENTS_LIMIT = 5;

export type RecentEntry = { id: string; label: string; href: string };

/// Scoped per workspace: "recently visited" means nothing across a workspace
/// boundary, and the stored hrefs are workspace-specific anyway.
export function recentsStorageKey(workspaceId: string): string {
  return `pw-command-recents:${workspaceId}`;
}

function isRecentEntry(value: unknown): value is RecentEntry {
  if (typeof value !== "object" || value === null) return false;
  const candidate = value as Record<string, unknown>;
  return (
    typeof candidate.id === "string" &&
    typeof candidate.label === "string" &&
    typeof candidate.href === "string"
  );
}

/// Storage is user-writable and survives deploys, so anything that is not the
/// current shape is treated as absent rather than thrown. A corrupt recents
/// list must never be able to break the menu.
export function parseRecents(raw: string | null): RecentEntry[] {
  if (!raw) return [];
  try {
    const parsed: unknown = JSON.parse(raw);
    if (!Array.isArray(parsed)) return [];
    return parsed.filter(isRecentEntry).slice(0, RECENTS_LIMIT);
  } catch {
    return [];
  }
}

export function pushRecent(list: RecentEntry[], entry: RecentEntry): RecentEntry[] {
  return [entry, ...list.filter((item) => item.id !== entry.id)].slice(0, RECENTS_LIMIT);
}

export function useRecentCommands(workspaceId: string): {
  recents: RecentEntry[];
  record: (entry: RecentEntry) => void;
} {
  const [recents, setRecents] = useState<RecentEntry[]>([]);

  useEffect(() => {
    try {
      setRecents(parseRecents(window.localStorage.getItem(recentsStorageKey(workspaceId))));
    } catch {
      setRecents([]);
    }
  }, [workspaceId]);

  const record = useCallback(
    (entry: RecentEntry) => {
      setRecents((previous) => {
        // Already the newest, so nothing to persist. Bailing here also keeps
        // the navigation recorder from re-rendering on every route re-resolve.
        if (previous[0]?.id === entry.id) return previous;
        const next = pushRecent(previous, entry);
        try {
          window.localStorage.setItem(recentsStorageKey(workspaceId), JSON.stringify(next));
        } catch {
          /* storage unavailable, keep the in-memory list */
        }
        return next;
      });
    },
    [workspaceId],
  );

  return { recents, record };
}
